import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import './AdminPanel.css';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [kpiData, setKpiData] = useState({
    todayRevenue: 0,
    yesterdayRevenue: 0,
    activeOrders: 0,
    urgentOrders: 0,
    dailyCustomers: 142,
    averageCustomers: 156,
    inventoryAlerts: 3,
    criticalItems: ['Oat Milk', 'Arabica Beans', 'Syrup Vanilla']
  });

  const [contactMessages, setContactMessages] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const [alerts] = useState([
    { id: 1, type: 'urgent', message: 'Check pending orders', time: '2 min ago' },
    { id: 2, type: 'warning', message: 'New customer messages', time: '5 min ago' },
    { id: 3, type: 'info', message: 'Inventory review needed', time: '8 min ago' }
  ]);

  const [quickStats] = useState({
    avgOrderValue: 18.75,
    avgOrderTrend: '+5.2%',
    tableEfficiency: 87,
    staffOnDuty: 6,
    waitlistCount: 4,
    kitchenQueue: 12
  });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch today's revenue and active orders
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Get today's date range
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // Get yesterday's date range
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        // Fetch today's orders
        const todayOrdersQuery = query(
          collection(db, 'orders'),
          where('createdAt', '>=', today),
          where('createdAt', '<', tomorrow)
        );
        const todayOrdersSnapshot = await getDocs(todayOrdersQuery);
        
        // Calculate today's revenue
        let todayRevenue = 0;
        let activeOrdersCount = 0;
        
        todayOrdersSnapshot.forEach(doc => {
          const order = doc.data();
          if (order.totalAmount) {
            todayRevenue += parseFloat(order.totalAmount);
          }
          if (order.status === 'pending' || order.status === 'preparing' || order.status === 'ready') {
            activeOrdersCount++;
          }
        });

        // Fetch yesterday's orders for comparison
        const yesterdayOrdersQuery = query(
          collection(db, 'orders'),
          where('createdAt', '>=', yesterday),
          where('createdAt', '<', today)
        );
        const yesterdayOrdersSnapshot = await getDocs(yesterdayOrdersQuery);
        
        let yesterdayRevenue = 0;
        yesterdayOrdersSnapshot.forEach(doc => {
          const order = doc.data();
          if (order.totalAmount) {
            yesterdayRevenue += parseFloat(order.totalAmount);
          }
        });

        // Fetch contact messages
        const contactQuery = query(
          collection(db, 'contactMessages'),
          orderBy('createdAt', 'desc'),
          limit(5)
        );
        const contactSnapshot = await getDocs(contactQuery);
        const messages = contactSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setKpiData(prev => ({
          ...prev,
          todayRevenue,
          yesterdayRevenue,
          activeOrders: activeOrdersCount,
          urgentOrders: Math.floor(activeOrdersCount * 0.3) // Rough estimate
        }));

        setContactMessages(messages);
        setLoadingData(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoadingData(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (amount) => `${amount.toFixed(2)} QAR`;
  const getChangePercentage = (current, previous) => {
    const change = ((current - previous) / previous) * 100;
    return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'urgent': return 'var(--urgent-red)';
      case 'warning': return 'var(--warning-amber)';
      case 'info': return 'var(--accent-color)';
      default: return 'var(--grey-medium)';
    }
  };

  return (
    <div className="admin-command-center">
      {/* Header with Time and Quick Status */}
      <div className="command-header">
        <div className="header-left">
          <h1 className="dashboard-title">Business Command Center</h1>
          <p className="current-time">{currentTime.toLocaleString()}</p>
        </div>
        <div className="header-right">
          <div className="quick-status">
            <span className="status-dot active"></span>
            <span>All Systems Operational</span>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="kpi-grid">
        <div className="kpi-card revenue">
          <div className="kpi-header">
            <h3>Today's Revenue</h3>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2v20m8-10H4" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="kpi-value">{formatCurrency(kpiData.todayRevenue)}</div>
          <div className={`kpi-change ${kpiData.todayRevenue > kpiData.yesterdayRevenue ? 'positive' : 'negative'}`}>
            {getChangePercentage(kpiData.todayRevenue, kpiData.yesterdayRevenue)} vs yesterday
          </div>
        </div>

        <div className="kpi-card orders">
          <div className="kpi-header">
            <h3>Active Orders</h3>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="kpi-value">{kpiData.activeOrders}</div>
          <div className="kpi-change urgent">{kpiData.urgentOrders} urgent orders</div>
        </div>

        <div className="kpi-card customers">
          <div className="kpi-header">
            <h3>Daily Customers</h3>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m3 5.197v1M13 7a4 4 0 11-8 0 4 4 0 018 0z" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="kpi-value">{kpiData.dailyCustomers}</div>
          <div className={`kpi-change ${kpiData.dailyCustomers > kpiData.averageCustomers ? 'negative' : 'positive'}`}>
            {kpiData.dailyCustomers - kpiData.averageCustomers} vs average
          </div>
        </div>

        <div className="kpi-card inventory">
          <div className="kpi-header">
            <h3>Inventory Alerts</h3>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div className="kpi-value">{kpiData.inventoryAlerts}</div>
          <div className="kpi-change critical">{kpiData.criticalItems.join(', ')}</div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        {/* Quick Actions Hub */}
        <div className="dashboard-card quick-actions">
          <div className="card-header">
            <h2>Quick Actions</h2>
          </div>
          <div className="quick-actions-grid">
            <button className="action-btn primary" onClick={() => navigate('/admin/orders')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Process New Order
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/admin/waiting-list')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Waitlist ({quickStats.waitlistCount})
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/admin/analytics')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Analytics Dashboard
            </button>
          </div>
        </div>

        {/* Real-Time Alerts */}
        <div className="dashboard-card alerts">
          <div className="card-header">
            <h2>Live Alerts</h2>
            <span className="alert-count">{alerts.length}</span>
          </div>
          <div className="alerts-list">
            {alerts.map(alert => (
              <div 
                key={alert.id} 
                className={`alert-item ${alert.type}`}
                onClick={() => navigate('/admin/orders')}
                style={{ cursor: 'pointer' }}
              >
                <div className="alert-dot" style={{ backgroundColor: getStatusColor(alert.type) }}></div>
                <div className="alert-content">
                  <p className="alert-message">{alert.message}</p>
                  <span className="alert-time">{alert.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Messages */}
        <div className="dashboard-card contact-messages">
          <div className="card-header">
            <h2>Recent Contact Messages</h2>
            <button className="view-all-btn" onClick={() => navigate('/admin/contact-messages')}>
              View All
            </button>
          </div>
          <div className="contact-messages-list">
            {loadingData ? (
              <div className="loading-state">Loading messages...</div>
            ) : contactMessages.length > 0 ? (
              contactMessages.map(message => (
                <div key={message.id} className="contact-message-item">
                  <div className="message-header">
                    <span className="message-name">{message.name}</span>
                    <span className="message-time">
                      {message.createdAt ? new Date(message.createdAt.toDate()).toLocaleDateString() : 'Recent'}
                    </span>
                  </div>
                  <div className="message-subject">{message.subject}</div>
                  <div className="message-preview">
                    {message.message ? message.message.substring(0, 100) + '...' : 'No message content'}
                  </div>
                  <div className="message-contact">
                    <span>📧 {message.email}</span>
                    {message.phone && <span>📱 {message.phone}</span>}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-messages">No recent messages</div>
            )}
          </div>
        </div>

        {/* Business Intelligence */}
        <div className="dashboard-card business-intel">
          <div className="card-header">
            <h2>Business Intelligence</h2>
            <button className="view-all-btn" onClick={() => navigate('/admin/salesreport')}>
              View Details
            </button>
          </div>
          <div className="intel-content">
            <div className="sales-overview">
              <h4>Sales Trend (Last 7 Days)</h4>
              <div className="trend-chart">
                {/* Simple bar chart representation */}
                <div className="chart-bars">
                  <div className="bar" style={{ height: '60%' }}></div>
                  <div className="bar" style={{ height: '75%' }}></div>
                  <div className="bar" style={{ height: '45%' }}></div>
                  <div className="bar" style={{ height: '90%' }}></div>
                  <div className="bar" style={{ height: '85%' }}></div>
                  <div className="bar" style={{ height: '70%' }}></div>
                  <div className="bar current" style={{ height: '95%' }}></div>
                </div>
                <div className="chart-labels">
                  <span>Mon</span>
                  <span>Tue</span>
                  <span>Wed</span>
                  <span>Thu</span>
                  <span>Fri</span>
                  <span>Sat</span>
                  <span>Sun</span>
                </div>
              </div>
            </div>
            <div className="top-products">
              <h4>Top Performers This Week</h4>
              <div className="product-list">
                <div className="product-item">
                  <span className="product-name">Flat White</span>
                  <span className="product-sales">287 sold</span>
                </div>
                <div className="product-item">
                  <span className="product-name">Avocado Toast</span>
                  <span className="product-sales">156 sold</span>
                </div>
                <div className="product-item">
                  <span className="product-name">Cappuccino</span>
                  <span className="product-sales">234 sold</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Operational Control */}
        <div className="dashboard-card operations">
          <div className="card-header">
            <h2>Operations Control</h2>
          </div>
          <div className="operations-grid">
            <div className="operation-status">
              <div className="status-header">
                <span>Kitchen Status</span>
                <div className="status-indicator active"></div>
              </div>
              <div className="status-details">
                <span>12 orders in queue</span>
                <span>Avg prep: 8 min</span>
              </div>
            </div>
            <div className="operation-status">
              <div className="status-header">
                <span>Payment Systems</span>
                <div className="status-indicator active"></div>
              </div>
              <div className="status-details">
                <span>All terminals online</span>
                <span>Daily total: {formatCurrency(kpiData.todayRevenue)}</span>
              </div>
            </div>
            <div className="operation-status">
              <div className="status-header">
                <span>Equipment</span>
                <div className="status-indicator warning"></div>
              </div>
              <div className="status-details">
                <span>Coffee Machine 2: Maintenance</span>
                <span>All other systems OK</span>
              </div>
            </div>
          </div>
        </div>

        {/* Smart Insights */}
        <div className="dashboard-card insights">
          <div className="card-header">
            <h2>Smart Insights</h2>
          </div>
          <div className="insights-content">
            <div className="insight-item">
              <div className="insight-icon">🌤️</div>
              <div className="insight-text">
                <p><strong>Weather Impact:</strong> Sunny weather expected. Anticipate 15% increase in iced drinks.</p>
              </div>
            </div>
            <div className="insight-item">
              <div className="insight-icon">📈</div>
              <div className="insight-text">
                <p><strong>Trend Alert:</strong> Oat Milk demand up 25% this week. Consider promoting oat-based drinks.</p>
              </div>
            </div>
            <div className="insight-item">
              <div className="insight-icon">💡</div>
              <div className="insight-text">
                <p><strong>Optimization:</strong> Table 4 has 20% longer dining times. Check service efficiency.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;